import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchGistData } from '../services/gistService';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const gistId = process.env.REACT_APP_GIST_ID;

      try {
        let jsonData = null;

        // Try fetching from Gist if configured
        if (gistId) {
          try {
            jsonData = await fetchGistData(gistId, null);
          } catch (gistError) {
            // Silently fall back to local data
          }
        }

        // Fallback to local file if Gist failed or not configured
        if (!jsonData) {
          const response = await fetch('/data/fallback.json');
          if (!response.ok) {
            throw new Error('Failed to fetch website data');
          }
          jsonData = await response.json();
        }

        if (jsonData) {
          const businessData = jsonData.business ? jsonData.business : jsonData;
          setData(businessData);

          if (businessData.name) {
            document.title = businessData.name;
          }

          const faviconUrl = businessData.images?.logo;
          if (faviconUrl) {
            const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
            link.rel = 'icon';
            link.href = faviconUrl;
            document.head.appendChild(link);

            const appleIcon = document.querySelector("link[rel='apple-touch-icon']") || document.createElement('link');
            appleIcon.rel = 'apple-touch-icon';
            appleIcon.href = faviconUrl;
            document.head.appendChild(appleIcon);
          }
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const value = {
    data,
    loading,
    error,
    // Helper functions to access deeply nested data
    getProducts: () => (data?.products?.items || []).filter(p => p.enabled !== false),
    getCategories: () => data?.products?.categories || [],
    getFeaturedCategories: () => (data?.products?.categories || []).filter(c => c.isFeatured),
    getServices: () => data?.services || [],
    getFeatures: () => data?.features || [],
    getTheme: () => data?.ui?.theme || {},
    getImages: () => data?.images || {},
    getContact: () => data?.contactInfo || {},
    getAbout: () => data?.about || {},
    getVision: () => data?.vision || {},
    getStatistics: () => data?.statistics || [],
    getUIText: (section) => data?.ui?.[section] || {},
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
